"use client";

import { useEffect } from "react";
import Link from "next/link";
import InstitutionalNav from "@/components/marketing/shared/InstitutionalNav";
import Footer from "@/components/landing/layouts/lumina/Footer";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { EVENT } from "@/lib/analytics-events";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("App error boundary caught:", error);
    trackEvent(EVENT.error_shown, {
      error_code: error.digest ?? error.name ?? "unknown",
      route:
        typeof window !== "undefined" ? window.location.pathname : undefined,
    });
  }, [error]);

  return (
    <div className="relative min-h-screen">
      <div
        aria-hidden
        className="fixed inset-0 z-0 bg-[color:var(--zom-public-page-bg)]"
      />
      <InstitutionalNav position="fixed" />
      <main className="relative z-10 mx-auto flex min-h-[70vh] max-w-5xl flex-col items-center justify-center px-6 pt-32 pb-20 text-center lg:pt-40">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_40%_at_50%_0%,color-mix(in_srgb,var(--zom-accent-primary)_9%,transparent)_0%,transparent_60%),radial-gradient(ellipse_60%_40%_at_20%_20%,color-mix(in_srgb,var(--zom-accent-secondary)_5%,transparent)_0%,transparent_45%)]" />
        <p className="font-body mb-3 text-xs font-medium text-[color:var(--zom-public-muted)] uppercase">
          Something went wrong
        </p>
        <h1 className="font-display mb-4 text-4xl leading-[1.1] font-light text-[color:var(--zom-public-ink)] md:text-5xl lg:text-6xl">
          We&apos;re resetting the signal.
        </h1>
        <p className="font-body mb-6 max-w-2xl text-base leading-relaxed text-[color:var(--zom-public-copy)] md:text-lg">
          The page encountered an error. Try again or return home and we&apos;ll
          help you get back on track.
        </p>
        {error?.message ? (
          <p className="font-body mb-8 text-sm text-[color:var(--zom-public-muted)]">
            {error.message}
          </p>
        ) : null}
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <Button
            onClick={reset}
            className="font-body rounded-full px-6 py-3 text-sm font-medium shadow-[0_12px_30px_-22px_rgba(15,23,42,0.25)]"
          >
            Try again
          </Button>
          <Link
            href="/"
            className="font-body flex items-center justify-center rounded-full border border-[var(--border)] px-6 py-3 text-sm font-medium text-[color:var(--zom-public-ink)] transition-all duration-300 hover:border-[color:var(--zom-accent-primary)] hover:text-[color:var(--zom-accent-primary)]"
          >
            Back to home
          </Link>
        </div>
      </main>
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
}
